import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BrandLogo } from "@/components/BrandLogo";
import { ArrowRight, TrendingUp, BarChart3, Brain, Zap, CheckCircle, Activity, Globe, MessageCircle, Users2, TrendingDown, Phone, Clock, HelpCircle, Timer } from "lucide-react";

export function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  // Force deploy - TendencIA branding update

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const handleGoToDashboard = () => {
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-figma-primary via-figma-primary/90 to-figma-primary">
      {/* Hero Section with animated background */}
      <div className="relative overflow-hidden min-h-screen flex items-center">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-figma-primary/20 via-white/10 to-figma-primary/20 animate-pulse" />
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <main className="container mx-auto px-4 py-12 relative z-10 w-full">
          <header className="flex flex-col items-center text-center mb-20">
            {/* Logo with glow effect */}
            <div className="relative mb-12">
              <div className="absolute inset-0 bg-white/20 blur-3xl rounded-full" />
              <div className="relative z-10 drop-shadow-2xl">
                <BrandLogo size="xl" />
              </div>
            </div>
            
            {/* Main heading with gradient text */}
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">
              Sistema de Monitoreo
            </h1>
            <h2 className="text-2xl md:text-3xl font-light text-white/90 mb-8">
              Estadísticas de Asistentes
            </h2>
            
            {/* Enhanced description */}
            <p className="text-xl md:text-2xl text-white/90 max-w-4xl leading-relaxed mb-12">
              Monitorea las estadísticas de tus asistentes: llamadas, conversaciones, tiempo promedio, horas de actividad y efectividad de ayuda a usuarios
            </p>
            
            {/* CTA button */}
            <div className="flex justify-center">
              <Button 
                size="lg" 
                onClick={handleGoToDashboard}
                className="group bg-figma-primary/20 backdrop-blur-sm text-white hover:bg-figma-primary/30 hover:text-white px-12 py-6 text-xl font-bold shadow-2xl hover:shadow-white/30 transition-all duration-300 rounded-2xl border-2 border-white/50"
              >
                Acceder
                <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </header>

          {/* Key capabilities - simple and premium */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="text-center">
              <div className="text-4xl font-light text-white mb-2">Llamadas</div>
              <p className="text-white/80">Estadísticas detalladas</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-white mb-2">Tiempo Promedio</div>
              <p className="text-white/80">Análisis de duración</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-light text-white mb-2">Efectividad</div>
              <p className="text-white/80">Ayuda a usuarios</p>
            </div>
          </div>
        </main>
      </div>

      {/* Features Section - What viewers can do */}
      <section className="py-20 px-4 bg-white/10 backdrop-blur-sm">
        <div className="container mx-auto">
          <h2 className="text-4xl font-light text-center text-white mb-16">
            Funcionalidades
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="bg-white/25 backdrop-blur-md border border-white/30 hover:bg-white/35 transition-all duration-300 shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <Phone className="w-6 h-6 text-figma-primary" />
                </div>
                <CardTitle className="text-lg text-slate-900 font-semibold">Estadísticas de Llamadas</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-800 font-medium">
                  Volumen, duración y frecuencia de llamadas
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-white/25 backdrop-blur-md border border-white/30 hover:bg-white/35 transition-all duration-300 shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <MessageCircle className="w-6 h-6 text-figma-primary" />
                </div>
                <CardTitle className="text-lg text-slate-900 font-semibold">Conversaciones</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-800 font-medium">
                  Historial completo y análisis de interacciones
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-white/25 backdrop-blur-md border border-white/30 hover:bg-white/35 transition-all duration-300 shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <Timer className="w-6 h-6 text-figma-primary" />
                </div>
                <CardTitle className="text-lg text-slate-900 font-semibold">Tiempo Promedio</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-800 font-medium">
                  Duración promedio de sesiones y respuestas
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-white/25 backdrop-blur-md border border-white/30 hover:bg-white/35 transition-all duration-300 shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <HelpCircle className="w-6 h-6 text-figma-primary" />
                </div>
                <CardTitle className="text-lg text-slate-900 font-semibold">Efectividad de Ayuda</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-800 font-medium">
                  Porcentaje de usuarios satisfechos y problemas resueltos
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What viewers can see - Premium presentation */}
      <section className="py-20 px-4 bg-gradient-to-b from-figma-primary/90 to-figma-primary">
        <div className="container mx-auto">
          <h2 className="text-4xl font-light text-center text-white mb-16">
            Lo que verás en el Sistema de Monitoreo
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {/* Dashboard Section */}
            <div className="space-y-4">
              <h3 className="text-2xl font-light text-white mb-4">Estadísticas de Llamadas y Tiempo</h3>
              <ul className="space-y-3 text-white/90">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Total de llamadas recibidas y realizadas</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Duración promedio de cada interacción</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Horas de actividad del asistente</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Tiempo de respuesta del asistente</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Frecuencia de uso por períodos</span>
                </li>
              </ul>
            </div>
            
            {/* Conversations & Effectiveness Section */}
            <div className="space-y-4">
              <h3 className="text-2xl font-light text-white mb-4">Conversaciones y Efectividad</h3>
              <ul className="space-y-3 text-white/90">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Historial completo de conversaciones</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Porcentaje de usuarios que recibieron ayuda</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Nivel de satisfacción de usuarios</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Problemas resueltos vs no resueltos</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Análisis de temas más consultados</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Simple and premium */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-light text-figma-primary mb-8">
              Sistema de Monitoreo está listo para ti
            </h2>
            
            <div className="flex justify-center">
              <Button 
                size="lg" 
                onClick={handleGoToDashboard}
                className="group bg-figma-primary hover:bg-figma-primary/90 text-white px-16 py-6 text-xl font-light shadow-xl hover:shadow-figma-primary/30 transition-all duration-300 rounded-xl"
              >
                Acceder ahora
                <ArrowRight className="w-6 h-6 ml-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Minimal and elegant */}
      <footer className="bg-figma-primary border-t border-figma-primary/20">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="mb-6">
              <p className="font-light text-white text-2xl mb-2">Sistema de Monitoreo</p>
              <p className="text-white/70 text-sm">by TendencIA</p>
            </div>
            
            <div className="border-t border-white/20 pt-6">
              <p className="text-white/60 text-sm">
                © 2025 TendencIA. Todos los derechos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}